import { logService, LOG_CATEGORIES } from '@/services/logService'
import { isTauriMobile } from '@/services/platformService'

import type { Router } from 'vue-router'

/**
 * Android hardware back-button handling.
 *
 * On Tauri Android the generated `WryActivity` already maps the hardware back
 * button to the WebView's history: `canGoBack() ? goBack() : finish()`. Because
 * vue-router uses `createWebHistory` (real `pushState` history), this already
 * gives the platform-correct behaviour out of the box:
 *
 *   - back with route history  → navigate one step back
 *   - back at the root entry    → exit the app (return to launcher)
 *
 * Looping back to a fixed "home" screen when there is no history would trap the
 * user in the app (they could never leave with the back gesture), so we keep the
 * native exit behaviour and do NOT override it.
 *
 * What the native handler can't do is close transient UI (dialogs, bottom
 * sheets, the search palette) *before* navigating the route. That is the gap
 * this service fills: open overlays register a back handler, and a single
 * router guard closes the top-most overlay instead of letting the back gesture
 * pop the route. When no overlay is registered, navigation proceeds untouched
 * and the native default (history back / exit) applies.
 */

export type AndroidBackHandler = () => void

interface BackHandlerEntry {
  handler: AndroidBackHandler
}

// LIFO stack: the most recently opened overlay is closed first.
const handlerStack: BackHandlerEntry[] = []

let interceptorInstalled = false

/**
 * Registers a handler that should run when the Android back button is pressed
 * while the associated overlay is open. Returns an unregister function.
 */
export function registerAndroidBackHandler(handler: AndroidBackHandler): () => void {
  const entry: BackHandlerEntry = { handler }
  handlerStack.push(entry)

  return () => {
    const index = handlerStack.indexOf(entry)
    if (index !== -1) {
      handlerStack.splice(index, 1)
    }
  }
}

export function hasAndroidBackHandlers(): boolean {
  return handlerStack.length > 0
}

/**
 * Runs the most recently registered back handler. Returns true if one ran.
 */
export function runTopAndroidBackHandler(): boolean {
  const entry = handlerStack[handlerStack.length - 1]
  if (!entry) {
    return false
  }

  entry.handler()
  return true
}

/**
 * vue-router stores an incrementing `position` counter in `history.state`. A
 * back/pop navigation lands on an entry with a lower position than the one we
 * were on, which lets us distinguish a back gesture from a forward push without
 * relying on native events.
 */
function currentHistoryPosition(): number {
  const state = window.history.state as { position?: number } | null
  return typeof state?.position === 'number' ? state.position : 0
}

/**
 * Installs the single global back interceptor. No-op off Tauri mobile and on
 * repeat calls.
 */
export function installAndroidBackInterceptor(router: Router): void {
  if (interceptorInstalled || !isTauriMobile()) {
    return
  }
  interceptorInstalled = true

  let lastPosition = currentHistoryPosition()

  router.beforeEach(() => {
    const targetPosition = currentHistoryPosition()
    const isBackNavigation = targetPosition < lastPosition

    if (isBackNavigation && handlerStack.length > 0) {
      runTopAndroidBackHandler()
      // Abort the route change. vue-router restores the history position (it
      // navigates forward again to where we were), so the user stays on the
      // current screen while the overlay closes.
      return false
    }

    return true
  })

  router.afterEach(() => {
    lastPosition = currentHistoryPosition()
  })

  logService.info(LOG_CATEGORIES.UI, 'Android back interceptor installed')
}

/** Test-only: clears registered handlers and the install guard. */
export function __resetAndroidBackServiceForTests(): void {
  handlerStack.length = 0
  interceptorInstalled = false
}
